import React, { useRef } from 'react';
import { Form } from 'react-bootstrap';
import { FileText, Upload } from 'lucide-react';

interface CsvDropzoneProps {
  selectedFileName: string | null;
  uploading: boolean;
  onFileChange: (file: File) => void;
}

export interface CsvDropzoneHandle {
  reset: () => void;
}

export const CsvDropzone = React.forwardRef<CsvDropzoneHandle, CsvDropzoneProps>(
  ({ selectedFileName, uploading, onFileChange }, ref) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => ({
      reset: () => {
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    }));

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) onFileChange(file);
    };

    return (
      <div className="mb-3">
        <label
          htmlFor="csv-file-input"
          className={`d-block border border-2 border-dashed rounded p-4 text-center ${uploading ? 'text-muted' : ''}`}
          style={{
            cursor: uploading ? 'not-allowed' : 'pointer',
            borderColor: selectedFileName ? '#198754' : '#dee2e6',
            backgroundColor: selectedFileName ? '#f0fdf4' : '#fafafa',
            transition: 'background-color 120ms ease, border-color 120ms ease',
          }}
        >
          {selectedFileName ? (
            <>
              <FileText size={28} className="mb-2 text-success" />
              <div className="fw-semibold">{selectedFileName}</div>
              <div className="text-muted small mt-1">Click to choose a different file</div>
            </>
          ) : (
            <>
              <Upload size={28} className="mb-2 text-secondary" />
              <div className="fw-semibold">Click to choose a CSV file</div>
              <div className="text-muted small mt-1">.csv files only</div>
            </>
          )}
        </label>
        <Form.Control
          ref={fileInputRef}
          id="csv-file-input"
          type="file"
          accept=".csv,text/csv"
          onChange={handleChange}
          disabled={uploading}
          className="d-none"
        />
      </div>
    );
  },
);

CsvDropzone.displayName = 'CsvDropzone';
